import {Thumbnails} from '../../../MediaCard';
import {getKeyForFilePath} from '../keys';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {BaseFetchParams} from '.';
import {mapCacheResultsByFilepath} from './mapCacheResultsByFilepath';

//
/**
 * This generic type is pretty confusing. Understanding the logic inside the function first is recommended.
 *
 * There is a simpler way to do this, but creates types that are even more confusing (IMHO)
 * See: https://github.com/wandb/core/pull/11000
 * The way forward is to probably to combine the annotation here and the type abstractions from #11000, but for the moment I'm preferring the code I find easier to understand.
 */
type FetchWithCacheParams = BaseFetchParams & {
  directAssetUrlPromisesByKey: Map<string, Promise<string>>;
  internalAssetUrlPromisesByKey: Map<string, Promise<string>>;
  thumbnailsAssetUrlPromisesByKey: Map<string, Promise<Thumbnails>>;
  fetchNew: (
    p: BaseFetchParams
  ) => Promise<
    [Map<string, string>, Map<string, string>, Map<string, Thumbnails>]
  >;
};

// Use a global cache to deduplicate requests (including ongoing ones)
export async function fetchWithCache({
  runSignature,
  filePaths,
  directAssetUrlPromisesByKey,
  internalAssetUrlPromisesByKey,
  thumbnailsAssetUrlPromisesByKey,
  fetchNew,
}: FetchWithCacheParams): Promise<
  [Map<string, string>, Map<string, string>, Map<string, Thumbnails>] | null[]
> {
  /**
   * Before fetching filter out any file paths that have already been requested
   * ...dedupe these using a Set
   */
  const unfetchedFilePaths = [
    ...new Set(
      filePaths.filter(filePath => {
        const key = getKeyForFilePath(runSignature, filePath);
        return (
          !directAssetUrlPromisesByKey.has(key) ||
          !directAssetUrlPromisesByKey.get(key)
        );
      })
    ),
  ];

  // If all the filepaths have been fetched, we can eject early
  // return the awaited results from the in-memory-caches
  if (!unfetchedFilePaths.length) {
    return [
      await mapCacheResultsByFilepath(
        directAssetUrlPromisesByKey,
        filePaths,
        runSignature
      ),
      await mapCacheResultsByFilepath(
        internalAssetUrlPromisesByKey,
        filePaths,
        runSignature
      ),
      await mapCacheResultsByFilepath(
        thumbnailsAssetUrlPromisesByKey,
        filePaths,
        runSignature
      ),
    ];
  }

  /**
   * We need to update the cache with a promise that will resolve to a value that can be inspected for the filePath in question. This is because in the time that the promise is pending, this function can be called again with overlapping filePaths. We don't need to kick off new requests, but those downstream calls will need the ability to resolve the promise within their own scope
   *
   * 1a: fetch filepaths [a,b,c], each file path is new
   * 1b: immediately update the cache for each key to Map<filePathKey, Promise<fetch[a,b,c]>>
   * 2a: fetch filepaths [b,c,d], only d is new
   * 2b: to resolve the value for fetch 2 we need to hook into the in-flight promises for [b,c]
   *
   * Returns Map<filePath, directUrl>
   * NOTE: a filePath and the returned file.name are the same value
   */
  const promiseForUnfetchedFiles = fetchNew({
    runSignature,
    filePaths: unfetchedFilePaths,
  });

  /**
   * Now because this function could be hit multiple times while we're resolving the unfetched paths, we update the cache to to reflect that these filepaths have fetches in-flight so that `unfetchedFilePaths` won't include these filePaths
   */
  for (const filePath of unfetchedFilePaths) {
    const key = getKeyForFilePath(runSignature, filePath);
    directAssetUrlPromisesByKey.set(
      key,
      // eslint-disable-next-line no-async-promise-executor
      new Promise(async res => {
        /**
         * Note: each promise resolves to the value for ALL the unfetched filepaths in its loop because a single request is made for all the paths at once. These results come back as a Map, so we can easily `.get` the specific value for each entry
         */
        const [directURLs, internalURLs, thumbnails] =
          await promiseForUnfetchedFiles;

        // this is super hacky - just sticking the internal URL on the injected cache object if it's available
        const internalURL = internalURLs?.get(filePath);

        // NOTE: there is a condition where this promise will never resolve
        // this has been left to remain consistent with previous condition
        // TO DO: figure out how to handle this
        if (internalURL) {
          internalAssetUrlPromisesByKey.set(key, Promise.resolve(internalURL));
        }

        const thumbnail = thumbnails?.get(filePath);
        if (thumbnail) {
          thumbnailsAssetUrlPromisesByKey.set(key, Promise.resolve(thumbnail));
        }

        // if we're missing the directURL provide a falsy value we can filter out later
        res(directURLs?.get(filePath) || '');
      })
    );
  }

  /**
   * Once the fetching resolves for each constituent filePath, we remove the key/value pair from the cache for anything that didn't resolve successfully. This can happen on backend errors that return null for values
   * Request: fetch[a,b,c]
   * Backend: A:Result, B:Null, C:Result
   * Returns: [Val(A), Val(C)]
   * So we remove the cache entry for B so it retries (e.g. on component remount or when another panel with same underlying data accesses this). This will clean up entries in the cache that would otherwise be left as permanently pending promises from the last step
   */
  const [directURLsByKey] = await promiseForUnfetchedFiles;
  for (const filePath of unfetchedFilePaths) {
    const key = getKeyForFilePath(runSignature, filePath);
    /**
     * If the fetch doesn't return a value for some reason, or if the value returned is falsy, remove the value from the cache so that it can be fetched again.
     * NOTE: this is preserving poorly understood legacy behavior.
     * NOTE: this doesn't trigger any kind of retry behavior, and because of the race condition with panel mounting and the request it's likely that multiple panels might all be downstream of this failed value
     * TODO: add a retry for these?
     */
    if (!directURLsByKey?.has(filePath) || !directURLsByKey?.get(filePath)) {
      directAssetUrlPromisesByKey.delete(key);
      internalAssetUrlPromisesByKey.delete(key);
      thumbnailsAssetUrlPromisesByKey.delete(key);
    }
  }

  /**
   * We've already awaited `promiseForUnfetchedFiles` so now we have all the results. Look up each value in the cache and inspect the results. Since the cache is <string, Promise<string>> we have to resolve the promises - some of the filepaths could be in-flight promises from another request
   *
   * Transform path-specific data into the actual form that the cached function spits out.
   */
  const [directURLsByFilePath, internalURLsByFilePath, thumbnailsByFilePath] =
    await Promise.all([
      mapCacheResultsByFilepath(
        directAssetUrlPromisesByKey,
        filePaths,
        runSignature
      ),
      mapCacheResultsByFilepath(
        internalAssetUrlPromisesByKey,
        filePaths,
        runSignature
      ),
      mapCacheResultsByFilepath(
        thumbnailsAssetUrlPromisesByKey,
        filePaths,
        runSignature
      ),
    ]);

  /**
   * If a value coming back is undefined or null that means the underlying fetch failed. We're returning `null` here to be consistent with the previous implementation. This was assumed to be safer for reasons I don't understand.
   * TODO: research the edge cases and figure out how to handle individual failed results
   *
   * NOTE: since the `unfetchedFilePaths` function above now filters out falsy values so we can refetch, this might not be a big issue
   */
  if ([...directURLsByFilePath.values()].some(e => e == null)) {
    return [null, null, null];
  }

  return [directURLsByFilePath, internalURLsByFilePath, thumbnailsByFilePath];
}
